import React, { Ref, useEffect, useState } from 'react';
import {
  FlatList, Pressable, StyleSheet, Text, View, TextInput, ActivityIndicator, Platform,
} from 'react-native';
import { shallowEqual, useDispatch, useSelector } from 'react-redux';
import { FontAwesome } from '@expo/vector-icons';
import { useNavigation } from '@react-navigation/native';
import Modal from 'react-native-modal';
import { useSafeAreaInsets } from 'react-native-safe-area-context';
import ViewShot from 'react-native-view-shot';
import AsyncStorage from '@react-native-async-storage/async-storage';
import Style from '../../../constants/Style';
import Colors from '../../../constants/Colors';
import { generateAvatarColor } from '../../Tickets/data';
import {
  getNotificationStatusBackgroundColor, getNotificationStatusColor, getNotificationStatusText,
} from '../../../components/NotificationsLog/data';
import getUsers from '../../../requests/Users/GetUsers';
import Button from '../../../components/Button';
import FilterIcon16 from '../../../components/16pxIcons/FilterIcon';
import TicketsFilter from '../../Tickets/components/TicketsFilter';
import getUserGroups from '../../../requests/Users/GetUserGroups';
import { DateRange } from '../../../redux/dateRangeSlice';
import getTickets from '../../../requests/Tickets/GetTickets';
import isMobile from '../../../components/IsMobile';
import CloseModalIcon from '../../../components/CloseModalIcon';
import { logEvent } from '../../../components/LogEvent';
import AvatarIcon from '../../../components/AvatarIcon';
import { getInitials } from '../../Tickets/components/TicketAssigneeIcon';
import {
  AssetInstance, DEFAULT_TICKET_PRIORITY, Ticket, ticketPriorityIsValid, UserInstance,
  UserStatus,
} from '../../../constants/Types';
import PressableStyled, { PressableState } from '../../../components/PressableStyled';
import { updateSelectedTicket } from '../../../redux/selectedTicketSlice';
import getAssets from '../../../requests/Assets/GetAssets';
import { TicketsFilterState } from '../../../redux/ticketsFilterSlice';
import WidgetHeader from './WidgetHeader';
import SearchIcon from '../../../components/SearchIcon';
import DropdownMenu from '../../../components/DropdownMenu';
import getTicketStats from '../../../requests/Tickets/GetTicketStats';
import TimeOnTaskIcon from '../../../components/24pxIcons/TimeOnTaskIcon';
import ResponseTimeIcon from '../../../components/24pxIcons/ResponseTimeIcon';
import { AppScreen } from '../../../navigation/LinkingConfiguration';
import TicketFilterTagSection from '../../Tickets/components/TicketFilterTagSection';
import { BuildingSettingsPriority } from '../../LocationManagement/components/BuildingSettingsModal/data';
import { Building } from '../../LocationManagement/components/AddBuildingModal';
import PriorityLabel from '../../LocationManagement/components/PriorityLabel';
import { getUserFullName } from '../../../constants/Utils';

interface TicketDetailsProps {
  range: DateRange,
  compareRange: DateRange,
  buildingId: string,
  selectedFloorId: string,
  selectedAreaId: string,
  selectedSpaceId: string,
  userGroupId: string,
  viewShotRef: Ref<ViewShot>,
}

interface TicketItemProps {
  ticket: Ticket,
  isComparison?: boolean,
}

const formatTime = (time: number) => {
  if (!Number.isFinite(time)) return '-';
  const minutes: number = Math.floor(time / 60000);
  const hours: number = Math.floor(minutes / 60);
  return `${hours < 10 ? '0' : ''}${hours}:${minutes % 60 < 10 ? '0' : ''}${minutes % 60}`;
};

const TicketDetails = ({
  range, compareRange, buildingId, selectedFloorId, selectedAreaId, selectedSpaceId, userGroupId,
  viewShotRef,
}: TicketDetailsProps) => {
  const dispatch = useDispatch();
  const [tickets, setTickets] = useState<Ticket[]>(undefined);
  const [ticketsCompared, setTicketsCompared] = useState<Ticket[]>(undefined);
  const [ticketCount, setTicketCount] = useState<number>(0);
  const [ticketCountCompared, setTicketCountCompared] = useState<number>(0);
  const [pageNumber, setPageNumber] = useState<number>(0);
  const [searchValue, setSearchValue] = useState<string>('');
  const [debouncedSearchValue, setDebouncedSearchValue] = useState<string>('');
  const selectedBuilding: { SlaHours: BuildingSettingsPriority[] } = useSelector(
    (state: { selectedBuilding: { SlaHours: BuildingSettingsPriority[] } }) => state
      .selectedBuilding,
  );
  const [otherPriorityFilter, setOtherPriorityFilter] = useState<boolean>(false);
  const [lowPriorityFilter, setLowPriorityFilter] = useState<boolean>(false);
  const [mediumPriorityFilter, setMediumPriorityFilter] = useState<boolean>(false);
  const [highPriorityFilter, setHighPriorityFilter] = useState<boolean>(false);
  const [criticalPriorityFilter, setCriticalPriorityFilter] = useState<boolean>(false);
  const slaNames: string[] = useSelector((state: {
    selectedBuilding: Building
  }) => state.selectedBuilding.SlaHours
    ?.map((item: BuildingSettingsPriority) => item.Name), shallowEqual);
  const selectedBuildingId: string = useSelector((state:
    { selectedBuilding: { id: string } }) => state.selectedBuilding.id);
  const [slaSort, setSlaSort] = useState<string>('');
  const [selectedAssignee, setSelectedAssignee] = useState<string>('');
  const [selectedAssigneeName, setSelectedAssigneeName] = useState<string>('');
  const [selectedAssetId, setSelectedAssetId] = useState<string>('');
  const [selectedAssetName, setSelectedAssetName] = useState<string>('');
  const [saveFilterToggled, setSaveFilterToggled] = useState<boolean>(false);
  const [userGroups, setUserGroups] = useState([]);
  const [users, setUsers] = useState<UserInstance[]>([]);
  const [loading, setLoading] = useState<boolean>(false);
  const [usersObj] = useState({});
  const [openSelected, setOpenSelected] = useState<boolean>(false);
  const [inProgressSelected, setInProgressSelected] = useState<boolean>(false);
  const [resolvedSelected, setResolvedSelected] = useState<boolean>(false);
  const [slaBreachedSelected, setSlaBreachedSelected] = useState<boolean>(false);
  const [responseOverdueSelected, setResponseOverdueSelected] = useState<boolean>(false);
  const [openCount, setOpenCount] = useState<number>(0);
  const [inProgressCount, setInProgressCount] = useState<number>(0);
  const [resolvedCount, setResolvedCount] = useState<number>(0);
  const [openCountCompared, setOpenCountCompared] = useState<number>(0);
  const [inProgressCountCompared, setInProgressCountCompared] = useState<number>(0);
  const [resolvedCountCompared, setResolvedCountCompared] = useState<number>(0);
  const [overdueCount, setOverdueCount] = useState<number>(0);
  const [filterShown, setFilterShown] = useState<boolean>(false);
  const [filterBackdropCloseEnabled, setFilterBackdropCloseEnabled] = useState<boolean>(true);
  const [time, setTime] = useState<Date>(new Date());
  const [assets, setAssets] = useState<AssetInstance[]>([]);
  const [rowsDropdownShown, setRowsDropdownShown] = useState<boolean>(false);
  const [listSize, setListSize] = useState<number>(5);
  const [currentTimeout, setCurrentTimeout] = useState(null);
  const locationId: string = selectedSpaceId ?? selectedAreaId ?? selectedFloorId ?? buildingId;
  const maxPageNumber: number = Math
    .max(Math.ceil(ticketCount / listSize), Math.ceil(ticketCountCompared / listSize)) - 1;
  const offset: number = pageNumber * listSize;
  const displayedCount: number = Math.max(ticketCount, ticketCountCompared);
  const navigation = useNavigation();
  const { top, bottom } = useSafeAreaInsets();
  const filterState: TicketsFilterState = useSelector((state:
    { ticketsFilter: TicketsFilterState }) => state.ticketsFilter);

  const appliedFilterCount: number = [otherPriorityFilter, lowPriorityFilter,
    mediumPriorityFilter, highPriorityFilter, criticalPriorityFilter, !!selectedAssignee,
    !!selectedAssetId, !!slaSort].filter((item: boolean) => !!item).length;

  const compareEnabled: boolean = !!compareRange.start && !!compareRange.end;

  const getTicketsParams = {
    other: otherPriorityFilter,
    low: lowPriorityFilter,
    moderate: mediumPriorityFilter,
    high: highPriorityFilter,
    critical: criticalPriorityFilter,
    done: resolvedSelected,
    inprogress: openSelected || inProgressSelected,
    reported: openSelected,
    assignee: selectedAssignee,
    buildingId,
    assetId: selectedAssetId,
    usergroupIds: [userGroupId],
    locationId,
    overdue: slaBreachedSelected ? true : null,
    isResponseOverdue: responseOverdueSelected,
    offset,
    limit: listSize,
  };

  const loadTickets: () => void = () => {
    if (!(range.start && range.end && buildingId)) return;
    setTickets(undefined);
    getTickets({
      startTime: range.start,
      endTime: range.end,
      ...getTicketsParams,
      textSearch: debouncedSearchValue,
    }).then((res) => {
      setOverdueCount(res?.statistics?.overdue);
      setTicketCount(res?.count ?? 0);
      setTickets(res?.tickets);
    }).catch(() => setTickets(null))
      .finally(() => setLoading(false));
    getTicketStats({ buildingId, from: range.start, to: range.end }).then((res) => {
      setOpenCount(res?.statusCounts?.reported);
      setInProgressCount(res?.statusCounts?.inProgress);
      setResolvedCount(res?.statusCounts?.completed);
    });

    setTicketsCompared(undefined);
    if (!compareEnabled) return;
    getTickets({
      startTime: compareRange.start,
      endTime: compareRange.end,
      ...getTicketsParams,
      textSearch: debouncedSearchValue,
    }).then((res) => {
      setTicketCountCompared(res?.count ?? 0);
      setTicketsCompared(res?.tickets);
    }).catch(() => setTicketsCompared(null))
      .finally(() => setLoading(false));
    getTicketStats({ buildingId, from: compareRange.start, to: compareRange.end }).then((res) => {
      setOpenCountCompared(res?.statusCounts?.reported);
      setInProgressCountCompared(res?.statusCounts?.inProgress);
      setResolvedCountCompared(res?.statusCounts?.completed);
    });
  };

  const loadUserData = async () => {
    await AsyncStorage.getItem('saveFilterToggled').then((val) => {
      const saveFiltersEnabled: boolean = val === 'yes';
      setSaveFilterToggled(saveFiltersEnabled);
      if (saveFiltersEnabled) {
        setOtherPriorityFilter(filterState.otherPriority);
        setLowPriorityFilter(filterState.lowPriority);
        setMediumPriorityFilter(filterState.mediumPriority);
        setHighPriorityFilter(filterState.highPriority);
        setCriticalPriorityFilter(filterState.criticalPriority);
        setSelectedAssetId(filterState.selectedAssetId);
        setSelectedAssignee(filterState.selectedAssignee);
        setSlaSort(filterState.slaSort);
      } else resetAllFilters();
    });
    getUsers().then((res) => {
      if (res?.items?.length > 0) {
        setUsers(res.items
          .filter((user: UserInstance) => user.status === UserStatus.VERIFIED));
        res.items?.forEach((item: UserInstance) => {
          usersObj[item.id] = {
            firstName: item.firstName,
            lastName: item.lastName,
            email: item.email,
          };
        });
      }
    });
    getUserGroups().then((response) => {
      if (response?.UserGroups?.length > 0) setUserGroups(response.UserGroups);
    });
    getAssets({ locationId: selectedBuildingId, enableSubLocationsLookUp: true }).then((result) => {
      if (result?.assets?.length > 0) {
        setAssets(result.assets.sort((a, b) => a?.Name?.localeCompare(b.Name)));
      }
    });
  };

  useEffect(() => {
    setPageNumber(0);
    setLoading(true);
  }, [listSize, openSelected, inProgressSelected, resolvedSelected, slaBreachedSelected,
    debouncedSearchValue]);

  useEffect(() => {
    setLoading(true);
  }, [pageNumber]);

  useEffect(() => {
    if (loading) loadTickets();
  }, [loading]);

  useEffect(() => {
    loadTickets();
    const unsubscribe = navigation.addListener('focus', loadTickets);
    return unsubscribe;
  }, [range, compareRange, locationId, userGroupId, appliedFilterCount]);

  useEffect(() => {
    loadUserData();
    const unsubscribe = navigation.addListener('focus', loadUserData);
    return unsubscribe;
  }, []);

  useEffect(() => {
    const interval = setInterval(() => setTime(new Date()), 60000);
    return () => clearInterval(interval);
  }, []);

  useEffect(() => {
    if (!(saveFilterToggled)) return;
    setLowPriorityFilter(filterState.lowPriority);
    setMediumPriorityFilter(filterState.mediumPriority);
    setHighPriorityFilter(filterState.highPriority);
    setCriticalPriorityFilter(filterState.criticalPriority);
    setOtherPriorityFilter(filterState.otherPriority);
    setSelectedAssetId(filterState.selectedAssetId);
  }, [saveFilterToggled]);

  useEffect(() => {
    clearTimeout(currentTimeout);
    setCurrentTimeout(setTimeout(() => {
      if (searchValue?.length > 2 || searchValue?.length === 0) {
        setDebouncedSearchValue(searchValue);
      }
      clearTimeout(currentTimeout);
    }, 1000));
  }, [searchValue]);

  const ticketSlaBreached = (t: Ticket): boolean => {
    let { priority } = t;
    if (!ticketPriorityIsValid(priority)) priority = DEFAULT_TICKET_PRIORITY;
    const responseTime: number = (t.startedOn ? new Date(t.startedOn) : time)
      .getTime() - new Date(t.createdOn).getTime();
    const slaDetails = selectedBuilding?.SlaHours[priority];
    if (!slaDetails) return true;
    const resolutionCutoff: number = slaDetails.ResolutionHours * 3600000
      + slaDetails.ResolutionMinutes * 60000;
    let responseCutoff: number = t.responseDueDate ? new Date(t.responseDueDate).getTime() : null;
    if (!responseCutoff) {
      responseCutoff = new Date(t.createdOn).getTime()
        + (slaDetails.ResponseHours * 60 + slaDetails.ResponseMinutes) * 60000;
    }
    const resolutionTime: number = (t.completedOn ? new Date(t.completedOn) : time)
      .getTime() - new Date(t.createdOn).getTime();
    return resolutionCutoff < resolutionTime || responseCutoff < responseTime;
  };

  const resetAllFilters: () => void = () => {
    setOtherPriorityFilter(false);
    setLowPriorityFilter(false);
    setMediumPriorityFilter(false);
    setHighPriorityFilter(false);
    setCriticalPriorityFilter(false);
    setSlaSort('');
    setSelectedAssetId('');
    setSelectedAssignee('');
    setSelectedAssigneeName('');
    setLoading(true);
  };

  const renderFilter = () => <TicketsFilter
    {...{
      range,
      users,
      assets,
      setLoading,
      setFilterOpen: setFilterShown,
      setTickets,
      setSelectedTicket: () => { },
      userGroups,
      otherPriorityFilter,
      setOtherPriorityFilter,
      lowPriorityFilter,
      setLowPriorityFilter,
      mediumPriorityFilter,
      setMediumPriorityFilter,
      highPriorityFilter,
      setHighPriorityFilter,
      criticalPriorityFilter,
      setCriticalPriorityFilter,
      slaSort,
      setSlaSort,
      selectedAssignee,
      setSelectedAssignee,
      selectedAssigneeName,
      setSelectedAssigneeName,
      selectedAssetId,
      setSelectedAssetId,
      selectedAssetName,
      setSelectedAssetName,
      selectedUserGroupId: userGroupId,
      setSelectedUserGroupId: null,
      selectedLocation: locationId,
      setSelectedLocation: null,
      setSelectedFloor: () => { },
      tickets,
      slaNames,
      filterShown,
      saveFilterToggled,
      setSaveFilterToggled,
    }}
    hideDatePicker={!isMobile}
  />;

  const TicketItem = ({ ticket, isComparison }: TicketItemProps) => {
    if (!ticket) return null;
    let { priority } = ticket;
    if (!ticketPriorityIsValid(priority)) priority = DEFAULT_TICKET_PRIORITY;
    const statusColor: string = getNotificationStatusColor(ticket.status);
    const responseTime: number = (ticket.startedOn ? new Date(ticket.startedOn) : time)
      .getTime() - new Date(ticket.createdOn).getTime();
    const slaDetails = selectedBuilding?.SlaHours[priority];
    const responseTimeCutoff: number = slaDetails ? slaDetails.ResponseHours * 3600000
      + slaDetails.ResponseMinutes * 60000 : null;
    const resolutionTimeCutoff: number = slaDetails ? slaDetails.ResolutionHours * 3600000
      + slaDetails.ResolutionMinutes * 60000 : null;
    const creationTime: number = ticket?.createdOn
      ? new Date(ticket.createdOn).getTime() : null;
    const timeElapsed: number = (ticket?.completedOn
      ? new Date(ticket.completedOn).getTime() : time.getTime()) - creationTime;
    let timeOnTask: number = 0;
    if (ticket.startedOn) {
      // eslint-disable-next-line no-unused-vars
      timeOnTask = (ticket.completedOn ? new Date(ticket.completedOn) : time)
        .getTime() - new Date(ticket.startedOn).getTime();
    }
    // eslint-disable-next-line no-unused-vars
    const timeOnTaskCutoff: number = slaDetails ? slaDetails.ResolutionHours * 3600000
      + slaDetails.ResolutionMinutes * 60000 - responseTime : null;
    const assignee: string | undefined = getUserFullName(usersObj[ticket.assignee]);
    const isOverdue: boolean = resolutionTimeCutoff < timeElapsed;
    const notStarted: boolean = (!ticket.startedOn && responseTime < resolutionTimeCutoff);

    return <Pressable
      style={({ hovered, pressed }: PressableState) => [
        !isMobile && Style.row, styles.item, isComparison && styles.itemCompared,
        hovered && Style.listItemHovered, pressed && Style.listItemPressed,
      ]}
      onPress={() => {
        dispatch(updateSelectedTicket({ ...ticket, pinned: true }));
        navigation.navigate(AppScreen.TICKETS as any);
      }}
    >
      {!isMobile && <View style={styles.prioritySection}>
        <PriorityLabel {...{ priority }} />
      </View>}
      <View style={styles.nameSection}>
        <Text style={styles.ticketName}>{ticket.name}</Text>
        <View style={styles.assetAndLocationNames}>
          <Text style={styles.assetName}>{ticket.assetName}</Text>
          <View style={styles.spot} />
          <Text style={styles.location}>
            {ticket.locationName ?? ticket.floorName}
          </Text>
        </View>
      </View>
      <View style={styles.statusSection}>
        <View style={[
          styles.statusIndicator,
          {
            borderColor: statusColor,
            backgroundColor: getNotificationStatusBackgroundColor(ticket.status),
          },
        ]}>
          <Text style={[styles.label, { color: statusColor }]}>
            {getNotificationStatusText(ticket.status)}
          </Text>
        </View>
        {isMobile && <PriorityLabel {...{ priority }} />}
      </View>
      <View style={styles.statusSection}>
        {isMobile && <View style={Style.row}>
          <ResponseTimeIcon style={styles.timeIcon} />
          <Text style={styles.label}>{'Response time:'}</Text>
        </View>}
        <View style={[
          styles.timeSection, (responseTimeCutoff < responseTime) && styles.overdue,
        ]}>
          <Text style={[styles.label, styles.time]}>
            {formatTime(responseTime)}
            {`${ticket.startedOn ? '' : '+'} / `}
            {formatTime(responseTimeCutoff)}
          </Text>
        </View>
      </View>
      <View style={styles.statusSection}>
        {isMobile && <View style={Style.row}>
          <TimeOnTaskIcon style={styles.timeIcon} />
          <Text style={styles.label}>{'Time on task:'}</Text>
        </View>}
        <View style={[
          styles.timeSection, isOverdue && styles.overdue, notStarted && styles.notStarted,
        ]}>
          <Text style={[styles.label, styles.time]}>
            {`${formatTime(timeElapsed)} / ${formatTime(resolutionTimeCutoff)}`}
          </Text>
        </View>
      </View>
      {!isMobile && <View style={[Style.row, styles.nameSection]}>
        <View style={[
          styles.userIcon,
          assignee && { backgroundColor: generateAvatarColor(assignee.length) },
        ]}>
          {assignee ? <Text style={[styles.label, styles.time]}>
            {getInitials(assignee)}
          </Text> : <AvatarIcon />}
        </View>
        <Text style={styles.label}>
          {assignee ?? 'Not assigned'}
        </Text>
      </View>}
    </Pressable>;
  };

  return <View
    testID='ticket-details-widget'
    style={[Style.commonCardStyle, { zIndex: 2 }]}
  >
    <WidgetHeader
      title='Ticket details'
      {...{ range, compareRange }}
      tooltipText='Summary of ticket information for the most recent tickets'
    />
    <ViewShot ref={viewShotRef}>
      {isMobile ? <View style={Style.row}>
        <View style={[Style.row, styles.searchBox]}>
          <SearchIcon />
          <TextInput
            placeholder='Search'
            placeholderTextColor={styles.location.color}
            style={[styles.label, styles.input]}
            value={searchValue}
            onChangeText={setSearchValue}
          />
        </View>
      </View> : <View style={Style.row}>
        <View style={styles.filterOptions}>
          <PressableStyled
            style={[
              Style.row, styles.filterOptionButton,
              openSelected && styles.filterOptionButtonSelected,
            ]}
            onPress={() => {
              setOpenSelected(!openSelected);
              setInProgressSelected(false);
              setResolvedSelected(false);
              setSlaBreachedSelected(false);
              setResponseOverdueSelected(false);
            }}
            testID='filter-by-open-btn'
          >
            <Text style={[styles.filterOptionText, openSelected && styles.time]}>
              {'Open'}
            </Text>
            <View style={styles.filterOptionIndicator}>
              <Text style={styles.filterOptionCount}>
                {openCount ?? '-'}
              </Text>
            </View>
            {compareEnabled
              && <View style={[styles.filterOptionIndicator, styles.filterOptionIndicatorCompared]}>
                <Text style={[styles.filterOptionCount, styles.filterOptionCountCompared]}>
                  {openCountCompared ?? '-'}
                </Text>
              </View>}
          </PressableStyled>
          <PressableStyled
            style={[
              Style.row, styles.filterOptionButton,
              inProgressSelected && styles.filterOptionButtonSelected,
            ]}
            onPress={() => {
              setOpenSelected(false);
              setInProgressSelected(!inProgressSelected);
              setResolvedSelected(false);
              setSlaBreachedSelected(false);
              setResponseOverdueSelected(false);
            }}
            testID='filter-by-in-progress-btn'
          >
            <Text style={[styles.filterOptionText, inProgressSelected && styles.time]}>
              {'In progress'}
            </Text>
            <View style={styles.filterOptionIndicator}>
              <Text style={styles.filterOptionCount}>
                {inProgressCount ?? '-'}
              </Text>
            </View>
            {compareEnabled
              && <View style={[styles.filterOptionIndicator, styles.filterOptionIndicatorCompared]}>
                <Text style={[styles.filterOptionCount, styles.filterOptionCountCompared]}>
                  {inProgressCountCompared ?? '-'}
                </Text>
              </View>}
          </PressableStyled>
          <PressableStyled
            style={[
              Style.row, styles.filterOptionButton,
              resolvedSelected && styles.filterOptionButtonSelected,
            ]}
            onPress={() => {
              setOpenSelected(false);
              setInProgressSelected(false);
              setResolvedSelected(!resolvedSelected);
              setSlaBreachedSelected(false);
              setResponseOverdueSelected(false);
            }}
            testID='filter-by-resolved-btn'
          >
            <Text style={[styles.filterOptionText, resolvedSelected && styles.time]}>
              {'Resolved'}
            </Text>
            <View style={styles.filterOptionIndicator}>
              <Text style={styles.filterOptionCount}>
                {resolvedCount ?? '-'}
              </Text>
            </View>
            {compareEnabled
              && <View style={[styles.filterOptionIndicator, styles.filterOptionIndicatorCompared]}>
                <Text style={[styles.filterOptionCount, styles.filterOptionCountCompared]}>
                  {resolvedCountCompared ?? '-'}
                </Text>
              </View>}
          </PressableStyled>
          <PressableStyled
            style={[
              Style.row, styles.filterOptionButton,
              slaBreachedSelected && styles.filterOptionButtonSelected,
            ]}
            onPress={() => {
              setOpenSelected(false);
              setInProgressSelected(false);
              setResolvedSelected(false);
              setSlaBreachedSelected(!slaBreachedSelected);
              setResponseOverdueSelected(false);
            }}
            testID='filter-by-sla-breached-btn'
          >
            <Text style={[styles.filterOptionText, slaBreachedSelected && styles.time]}>
              {'SLA breached'}
            </Text>
            {/* TODO: restore filter count */}
            {!!false
              && <View style={[styles.filterOptionIndicator, styles.overdueOptionIndicator]}>
                <Text style={[styles.filterOptionCount, styles.overdueCount]}>
                  {overdueCount ?? '-'}
                </Text>
              </View>}
          </PressableStyled>
          <PressableStyled
            style={[
              Style.row, styles.filterOptionButton,
              responseOverdueSelected && styles.filterOptionButtonSelected,
            ]}
            onPress={() => {
              setOpenSelected(false);
              setInProgressSelected(false);
              setResolvedSelected(false);
              setSlaBreachedSelected(false);
              setResponseOverdueSelected(!responseOverdueSelected);
            }}
            testID='response-overdue-btn'
          >
            <Text style={[styles.filterOptionText, responseOverdueSelected && styles.time]}>
              {'Response overdue'}
            </Text>
            {/* TODO: restore filter count */}
            {!!false
              && <View style={[styles.filterOptionIndicator, styles.overdueOptionIndicator]}>
                <Text style={[styles.filterOptionCount, styles.overdueCount]}>
                  {tickets?.filter((t: Ticket) => ticketSlaBreached(t)).length}
                </Text>
              </View>}
          </PressableStyled>
        </View>
        <Button
          title={'Filter  '}
          style={styles.filterButton}
          onPress={() => setFilterShown(isMobile ? true : !filterShown)}
          children={<View>
            <FilterIcon16 />
            {appliedFilterCount > 0 && <View style={styles.indicationDot} />}
          </View>}
          testID='filter-tickets-btn'
        />
      </View>}
      <TicketFilterTagSection
        {...{
          otherPriorityFilter,
          lowPriorityFilter,
          mediumPriorityFilter,
          highPriorityFilter,
          criticalPriorityFilter,
          selectedAssignee,
          selectedAssetId,
          slaSort,
          setOtherPriorityFilter,
          setLowPriorityFilter,
          setMediumPriorityFilter,
          setHighPriorityFilter,
          setCriticalPriorityFilter,
          setSelectedAssignee,
          setSelectedAssetId,
          setSlaSort,
          resetAllFilters,
          saveFilterToggled,
          setLoading,
          selectedAssetName,
          users,
        }}
      />
      <FlatList
        refreshing={loading}
        style={styles.list}
        ListEmptyComponent={tickets ? <Text style={styles.noTicketsText}>{'No tickets found'}</Text>
          : <ActivityIndicator
            style={Style.activityIndicator}
            size='large'
          />}
        contentContainerStyle={styles.listContent}
        ListHeaderComponent={isMobile ? null : <View style={[Style.row, styles.header]}>
          <Text style={[styles.prioritySection, styles.headerText]}>{'PRIORITY'}</Text>
          <Text style={[styles.nameSection, styles.headerText]}>{'NAME'}</Text>
          <Text style={[styles.statusSection, styles.headerText]}>{'STATUS'}</Text>
          <Text style={[styles.statusSection, styles.headerText]}>{'RESPONSE TIME'}</Text>
          <Text style={[styles.statusSection, styles.headerText]}>{'RESOLUTION TIME'}</Text>
          <Text style={[styles.nameSection, styles.headerText]}>{'ASSIGNEE'}</Text>
        </View>}
        data={[...Array(tickets?.length ?? 0).keys()]}
        renderItem={({ index }) => <View testID={`ticket#${index + 1}`}>
          <TicketItem ticket={tickets?.[index]} />
          <TicketItem
            ticket={ticketsCompared?.[index]}
            isComparison
          />
        </View>}
        keyExtractor={(index: number) => `ticket_details_item_${index}`}
      />
      <View style={styles.footer}>
        {!isMobile && <View style={styles.rowSizeSelection}>
          <Text style={styles.label}>{'Rows per page'}</Text>
          <DropdownMenu
            expanded={rowsDropdownShown}
            setExpanded={setRowsDropdownShown}
            options={[3, 4, 5, 10].map((size: number) => ({ value: size, name: size.toString() }))}
            selectedOption={({ value: listSize, name: listSize.toString() })}
            setSelectedOption={(option: { value: number }) => setListSize(option.value)}
            style={styles.listSizeDropdown}
            hideSelectAll
          />
        </View>}
        {tickets?.length > 0 && <FlatList
          horizontal
          testID='select-tickets-page'
          data={Array.from({ length: maxPageNumber + 1 }, (_, i) => i)}
          ListHeaderComponent={() => {
            const disabled: boolean = pageNumber <= 0;
            return <PressableStyled
              style={[
                styles.pageButton, styles.nextPageButton, disabled && styles.pageButtonDisabled,
              ]}
              onPress={() => setPageNumber(Math.max(0, pageNumber - 1))}
              onLongPress={() => setPageNumber(0)}
              {...{ disabled }}
              testID='previous-ticket-page-btn'
            >
              <FontAwesome
                name='angle-left'
                color={Colors.light.hereworksBlue500}
                size={20}
              />
            </PressableStyled>;
          }}
          ListFooterComponent={() => {
            const disabled: boolean = pageNumber >= maxPageNumber;
            return <PressableStyled
              style={[
                styles.pageButton, styles.nextPageButton, disabled && styles.pageButtonDisabled,
              ]}
              onPress={() => setPageNumber(Math.min(pageNumber + 1, maxPageNumber))}
              onLongPress={() => setPageNumber(maxPageNumber)}
              {...{ disabled }}
              testID='next-ticket-page-btn'
            >
              <FontAwesome
                name='angle-right'
                color={Colors.light.hereworksBlue500}
                size={20}
              />
            </PressableStyled>;
          }}
          renderItem={({ item }) => {
            if (Math.abs(item - pageNumber) > 2) return null;
            const selected: boolean = pageNumber === item;
            return <Pressable
              style={({ hovered, pressed }: PressableState) => [
                styles.pageButton, hovered && Style.hovered, pressed && Style.pressed,
                (hovered || pressed) && styles.pageButtonHovered,
                selected && styles.pageButtonSelected,
              ]}
              disabled={selected}
              onPress={() => setPageNumber(item)}
            >
              <Text style={[styles.label, selected && styles.time]}>{item + 1}</Text>
            </Pressable>;
          }}
          keyExtractor={(item: number) => `solve_analytics_tickets_page_number_${item + 1}`}
        />}
        {!isMobile && <Text style={styles.label}>
          {`${offset + 1}-${offset + Math.min(displayedCount, listSize)} of ${displayedCount} showing`}
        </Text>}
      </View>
      <Modal
        isVisible={filterShown}
        onBackButtonPress={() => setFilterShown(false)}
        onBackdropPress={() => (filterBackdropCloseEnabled ? setFilterShown(false) : null)}
        animationIn={isMobile ? 'fadeIn' : 'slideInRight'}
        animationOut={isMobile ? 'fadeOut' : 'slideOutRight'}
        onModalWillShow={() => setFilterBackdropCloseEnabled(false)}
        onModalShow={() => setFilterBackdropCloseEnabled(true)}
        style={Style.modal}
        statusBarTranslucent
      >
        {isMobile ? <View
          style={[styles.mobileFilterContainer, { marginTop: top + 12, paddingBottom: bottom }]}
        >
          <View style={[Style.row, styles.filterHeader]}>
            <PressableStyled
              onPress={() => {
                logEvent('Solve_Analytics', 'reset_tickets_widget_filters');
                resetAllFilters();
                setFilterShown(false);
              }}
            >
              <Text style={styles.resetTitle}>{'Reset'}</Text>
            </PressableStyled>
            <View style={{ flex: 1, alignItems: 'center' }}>
              <Text style={styles.filterTitle}>{'Filter'}</Text>
            </View>
            <PressableStyled
              onPress={() => {
                logEvent('Tickets', 'filter_close_button');
                setFilterShown(false);
              }}
            >
              <CloseModalIcon />
            </PressableStyled>
          </View>
          {renderFilter()}
        </View> : renderFilter()}
      </Modal>
    </ViewShot>
  </View>;
};

const styles = StyleSheet.create({
  headerText: {
    fontSize: 10,
    lineHeight: 16,
    color: Colors.light.hereworksBlack400,
    fontFamily: 'Roboto_500Medium',
    textTransform: 'uppercase',
  },
  prioritySection: {
    flex: 0.05,
    paddingHorizontal: 16,
    alignItems: 'flex-start',
    marginVertical: isMobile ? 2 : undefined,
  },
  nameSection: {
    flex: 0.3,
    paddingHorizontal: isMobile ? undefined : 16,
    justifyContent: 'flex-start',
    marginVertical: isMobile ? 2 : undefined,
  },
  statusSection: {
    ...Style.row,
    flex: 0.1,
    paddingHorizontal: isMobile ? undefined : 16,
    marginVertical: isMobile ? 4 : undefined,
  },
  header: {
    marginBottom: 12,
    height: 25,
  },
  item: {
    backgroundColor: Colors.light.hereworksWhite100,
    paddingVertical: isMobile ? 12 : 4,
    paddingHorizontal: isMobile ? 12 : undefined,
    marginVertical: 4,
    borderRadius: 10,
    minHeight: isMobile ? 100 : 40,
    justifyContent: 'space-between',
  },
  itemCompared: {
    backgroundColor: Colors.light.hereworksYellow50,
  },
  ticketName: {
    fontSize: isMobile ? 16 : 14,
    lineHeight: isMobile ? undefined : 21,
    letterSpacing: 0,
    fontFamily: isMobile ? 'Poppins_500Medium' : 'Roboto_400Regular',
    color: Colors.light.hereworksBlack600,
    flex: 1,
    marginEnd: 8,
  },
  label: {
    fontSize: 14,
    lineHeight: isMobile ? undefined : 21,
    letterSpacing: 0,
    fontFamily: 'Roboto_400Regular',
    color: Colors.light.hereworksBlack600,
  },
  location: {
    fontSize: 12,
    lineHeight: 18,
    fontFamily: 'Roboto_400Regular',
    color: Colors.light.hereworksBlack400,
  },
  assetName: {
    fontSize: 12,
    lineHeight: 19,
    fontFamily: 'Roboto_500Medium',
    color: Colors.light.hereworksBlue500,
  },
  statusIndicator: {
    borderWidth: 1,
    borderRadius: 4,
    paddingHorizontal: 8,
    paddingVertical: isMobile ? 1 : undefined,
    backgroundColor: '#FFF',
  },
  timeSection: {
    borderRadius: 4,
    paddingHorizontal: 4,
    backgroundColor: Colors.light.hereworksGreen400,
    alignItems: 'center',
    marginStart: isMobile ? 4 : undefined,
    paddingVertical: isMobile ? 1 : undefined,
  },
  overdue: {
    backgroundColor: Colors.light.hereworksRed500,
  },
  notStarted: {
    backgroundColor: Colors.light.hereworksPrimaryGrey800,
  },
  time: {
    color: '#FFF',
  },
  userIcon: {
    width: 32,
    height: 32,
    borderRadius: 16,
    marginEnd: 8,
    justifyContent: 'center',
    alignItems: 'center',
    backgroundColor: Colors.light.hereworksBlue50,
    overflow: 'hidden',
  },
  list: {
    marginTop: isMobile ? 12 : 24,
  },
  listContent: {
    minHeight: 250,
  },
  noTicketsText: {
    flex: 1,
    textAlign: 'center',
    paddingTop: '5%',
    fontSize: 16,
    lineHeight: 24,
    fontFamily: 'Roboto_400Regular',
    color: Colors.light.hereworksBlack600,
  },
  footer: {
    flexDirection: 'row',
    alignItems: isMobile ? 'stretch' : 'center',
    marginTop: 24,
  },
  rowSizeSelection: {
    flexDirection: 'row',
    flex: 1,
    alignItems: 'center',
  },
  pageButton: {
    width: 32,
    height: 32,
    borderRadius: 16,
    justifyContent: 'center',
    alignItems: 'center',
    marginHorizontal: 2,
  },
  nextPageButton: {
    borderWidth: 1,
    borderColor: Colors.light.hereworksBlue500,
    marginHorizontal: 8,
  },
  pageButtonSelected: {
    backgroundColor: Colors.light.hereworksBlue500,
  },
  pageButtonDisabled: {
    opacity: 0.4,
  },
  pageButtonHovered: {
    borderWidth: 1,
    borderColor: Colors.light.hereworksBlue500,
  },
  searchBox: {
    ...Style.row,
    flexDirection: 'row-reverse',
    borderRadius: isMobile ? 10 : 24,
    backgroundColor: '#FFF',
    borderColor: Colors.light.hereworksBlue100,
    borderWidth: 1.5,
    flex: 1,
    paddingHorizontal: 12,
    paddingVertical: Platform.OS === 'ios' ? 8 : 0,
  },
  input: {
    marginEnd: 4,
    lineHeight: isMobile ? undefined : 14,
    letterSpacing: 0,
    fontFamily: 'Poppins_400Regular',
    color: Colors.light.hereworksBlack400,
    outlineWidth: 0,
  },
  filterOptions: {
    flexDirection: 'row',
    alignItems: 'center',
    flexWrap: 'wrap',
  },
  filterOptionButton: {
    paddingHorizontal: isMobile ? 8 : 12,
    paddingVertical: isMobile ? 6 : 8,
    borderRadius: 4,
    borderWidth: 1,
    borderColor: Colors.light.hereworksBlue100,
    backgroundColor: Colors.light.hereworksWhite100,
    marginEnd: 8,
    marginBottom: isMobile ? 8 : undefined,
  },
  filterOptionText: {
    fontSize: 12,
    lineHeight: 18,
    fontFamily: 'Roboto_400Regular',
    color: Colors.light.hereworksBlack600,
    alignSelf: 'center',
  },
  filterOptionIndicator: {
    paddingHorizontal: 8,
    borderWidth: 1,
    borderColor: Colors.light.hereworksBlue100,
    backgroundColor: Colors.light.hereworksWhite500,
    borderRadius: 34,
    marginStart: 6,
  },
  filterOptionIndicatorCompared: {
    borderColor: Colors.light.hereworksYellow100,
    backgroundColor: Colors.light.hereworksYellow50,
  },
  overdueOptionIndicator: {
    borderColor: Colors.light.hereworksRed500,
    backgroundColor: Colors.light.hereworksRed50,
  },
  filterOptionCount: {
    fontSize: 12,
    lineHeight: 18,
    fontFamily: 'Roboto_400Regular',
    color: Colors.light.hereworksBlue500,
  },
  filterOptionCountCompared: {
    color: Colors.light.hereworksYellow600,
  },
  overdueCount: {
    color: Colors.light.hereworksRed500,
  },
  filterOptionButtonSelected: {
    backgroundColor: Colors.light.hereworksBlue500,
  },
  filterButton: {
    height: isMobile ? undefined : 32,
    paddingHorizontal: 12,
    marginTop: isMobile ? 16 : undefined,
  },
  mobileFilterContainer: {
    flex: 1,
    marginTop: 16,
    backgroundColor: '#FFF',
    borderTopStartRadius: 10,
    borderTopEndRadius: 10,
  },
  filterTitle: {
    marginLeft: 24,
    fontFamily: 'Poppins_500Medium',
    fontSize: 16,
    color: Colors.light.hereworksBlack600,
  },
  resetTitle: {
    fontFamily: 'Poppins_500Medium',
    fontSize: 12,
    color: Colors.light.hereworksBlack600,
  },
  filterHeader: {
    marginHorizontal: 24,
    marginVertical: 18,
  },
  assetAndLocationNames: {
    flexDirection: 'row',
    flexWrap: 'wrap',
    alignItems: 'center',
  },
  spot: {
    marginHorizontal: 8,
    width: 4,
    height: 4,
    borderRadius: 2,
    backgroundColor: Colors.light.hereworksBlue100,
  },
  listSizeDropdown: {
    marginStart: 8,
    width: 74,
  },
  timeIcon: {
    marginEnd: 8,
  },
  indicationDot: {
    position: 'absolute',
    top: -2,
    end: -2,
    width: 6,
    height: 6,
    borderRadius: 3,
    backgroundColor: Colors.light.hereworksRed500,
  },
});

export default TicketDetails;
